import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  History, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  Phone,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '@/context/auth';
import { useTriggerHistory } from '@/hooks/sms/useTriggers';
import { 
  TriggerExecutionWithCustomer, 
  EXECUTION_STATUS_LABELS,
  ExecutionStatus 
} from '@/types/sms-triggers';

interface TriggerExecutionHistoryProps {
  triggerId?: string;
}

const TriggerExecutionHistory: React.FC<TriggerExecutionHistoryProps> = ({
  triggerId
}) => {
  const { user } = useAuth();
  const { executions, loading, pagination, loadPage } = useTriggerHistory(triggerId, user);

  const getStatusBadge = (status: ExecutionStatus) => {
    switch (status) {
      case 'sent':
        return <Badge variant="default">Sent</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'scheduled':
        return <Badge variant="outline">Scheduled</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'skipped':
        return <Badge variant="secondary">Skipped</Badge>;
      case 'cancelled':
        return <Badge variant="outline">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const truncateMessage = (message: string | null | undefined, maxLength = 50) => {
    if (!message) return 'N/A';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Execution History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-4 bg-gray-200 rounded flex-1 animate-pulse" />
                <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
                <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Execution History
          </CardTitle>
          {executions.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {executions.length} execution{executions.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {executions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No executions found</p>
            <p className="text-sm">
              {triggerId 
                ? 'This trigger has not been executed yet.'
                : 'No triggers have been executed recently.'
              }
            </p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Rule</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Executed</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {executions.map((execution) => (
                  <TableRow key={execution.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {execution.customer_name || 'Unknown Customer'}
                        </div>
                        {execution.customer_phone && (
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {execution.customer_phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm">
                        {execution.rule_name || 'Unknown Rule'}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      {getStatusBadge(execution.status)}
                      {execution.skip_reason && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {execution.skip_reason}
                        </div>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(execution.scheduled_for)}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm">
                        {execution.executed_at 
                          ? formatDate(execution.executed_at)
                          : 'Not executed'
                        }
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm">
                        {formatCurrency(execution.cost)}
                        {execution.message_units && (
                          <div className="text-xs text-muted-foreground">
                            {execution.message_units} unit{execution.message_units !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      {execution.message_content && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Message Content</DialogTitle>
                              <DialogDescription>
                                Message sent to {execution.customer_name} ({execution.customer_phone})
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-medium mb-2">Details</h4>
                                <dl className="grid grid-cols-2 gap-2 text-sm">
                                  <dt className="text-muted-foreground">Status:</dt>
                                  <dd>{getStatusBadge(execution.status)}</dd>
                                  <dt className="text-muted-foreground">Scheduled:</dt>
                                  <dd>{formatDate(execution.scheduled_for)}</dd>
                                  {execution.executed_at && (
                                    <>
                                      <dt className="text-muted-foreground">Executed:</dt>
                                      <dd>{formatDate(execution.executed_at)}</dd>
                                    </>
                                  )}
                                  <dt className="text-muted-foreground">Cost:</dt>
                                  <dd>{formatCurrency(execution.cost)}</dd>
                                </dl>
                              </div>
                              
                              <div>
                                <h4 className="font-medium mb-2 flex items-center gap-2">
                                  <MessageSquare className="h-4 w-4" />
                                  Message
                                </h4>
                                <div className="bg-gray-50 rounded p-4">
                                  <p className="text-sm whitespace-pre-wrap">
                                    {execution.message_content}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <div className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.total_pages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadPage(pagination.page - 1)}
                    disabled={pagination.page <= 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadPage(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TriggerExecutionHistory;